import React, { useState } from 'react'
import { Link as MuiLink } from '@material-ui/core'
import { useIntl } from 'react-intl'
import messages from '@utils/messages'
import { makeStyles } from '@material-ui/styles'
import {
  SnackbarType,
  useGlobalSnackbar,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import { PdfDownloadDocument } from '@src/queries/CoaCertificatePDFQuery.generated'
import { VectorFileDownloadDocument } from '@src/queries/VectorDocumentQuery.generated'
import HandleMarkup from '@src/components/HandleMarkup'
import { ApolloConsumer } from 'react-apollo'
import CircleLoader from '@src/components/CircleLoader'
import { DocumentType } from './SectionLinks'
import { DocumentLink } from '@src/types/graphql-types'

const useStyles = makeStyles(() => ({
  link: {
    cursor: 'pointer',
  },
}))

interface DownloadCertificateProps {
  link: DocumentLink
  productNumber: string
}

const DownloadCertificate: React.FC<DownloadCertificateProps> = ({
  link,
  productNumber,
}) => {
  const classes = useStyles()
  const [downloading, setDownloading] = useState(false)
  const intl = useIntl()
  const { setGlobalSnackbarState } = useGlobalSnackbar()

  const handleDownloadZip = async (client) => {
    try {
      const metaData = link?.anchorTag?.split('_')
      setDownloading(true)
      const { errors, data } = await client.query({
        query: VectorFileDownloadDocument,

        variables: {
          input: {
            term: productNumber,
            type: 'VSEQ',
            owner: metaData[0],
            unit: metaData[1],
          },
        },
      })

      if (
        errors ||
        !data ||
        !data?.getVectorDocument?.fileContent ||
        data.error
      )
        throw Error()

      const fileContent = data?.getVectorDocument?.fileContent
      const fileName = data?.getVectorDocument?.fileName

      const bs = atob(fileContent)
      const arrayBuffer = new ArrayBuffer(bs.length)
      const zipArray = new Uint8Array(arrayBuffer)
      for (let i = 0; i < bs.length; i++) {
        zipArray[i] = bs.charCodeAt(i)
      }
      const ZipFile = new Blob([zipArray], { type: 'application/zip' })
      const downloadUrl = URL.createObjectURL(ZipFile)
      const aTagLink = document.createElement('a')
      aTagLink.href = downloadUrl

      aTagLink.setAttribute('download', fileName)
      aTagLink.click()
      setDownloading(false)
    } catch (error) {
      setDownloading(false)
      setGlobalSnackbarState({
        open: true,
        message: intl.formatMessage(messages.VECTOR_DOCUMENT_NOT_FOUND),
        variant: SnackbarType.Error,
      })
    }
  }

  const handleDownloadPdf = async (client) => {
    const isDocTypeCertificate = link?.key === DocumentType.Certificate

    try {
      const [owner, unit, ...rest] = link.anchorTag.split('_')
      const docid = rest.join('_')

      setDownloading(true)
      const { errors, data } = await client.query({
        query: PdfDownloadDocument,

        variables: {
          input: {
            term: productNumber,
            type: isDocTypeCertificate ? 'CERT' : 'VMAP',
            owner,
            unit,
            ...(isDocTypeCertificate ? { docid } : {}),
          },
        },
      })

      if (errors || !data || data.error) throw Error()

      const pdfArray = new Uint8Array(data.pdfDownload.length)
      for (let i = 0; i < data.pdfDownload.length; i++) {
        pdfArray[i] = data.pdfDownload.charCodeAt(i)
      }

      const pdfFile = new Blob([pdfArray], { type: 'application/pdf' })
      const pdfURL = window.URL.createObjectURL(pdfFile)

      const aTagLink = document.createElement('a')
      aTagLink.href = pdfURL
      const filename = link?.anchorTag
        ?.replace(/\.[^/.]+$/, '')
        ?.replace(/\./g, '-')
      aTagLink.setAttribute('download', filename)
      aTagLink.click()
      setDownloading(false)
    } catch (error) {
      setDownloading(false)
      setGlobalSnackbarState({
        open: true,
        message: isDocTypeCertificate
          ? intl.formatMessage(messages.BLUE_PRODUCT_CERTIFICATE_NOT_FOUND)
          : intl.formatMessage(messages.VECTOR_DOCUMENT_NOT_FOUND),
        variant: SnackbarType.Error,
      })
    }
  }

  const isPdfDocument =
    link?.key === DocumentType.Certificate ||
    (link?.key === DocumentType.Vector &&
      (link?.anchorTag || '').endsWith('.pdf'))

  return (
    <>
      <ApolloConsumer>
        {(client) => (
          <>
            <MuiLink
              onClick={() =>
                isPdfDocument
                  ? handleDownloadPdf(client)
                  : handleDownloadZip(client)
              }
              className={classes.link}
            >
              <HandleMarkup value={link.label} />
            </MuiLink>
          </>
        )}
      </ApolloConsumer>
      {downloading ? <CircleLoader /> : null}
    </>
  )
}

export default DownloadCertificate
